import queryString from 'query-string'
import { isEmpty } from 'lodash'

import { fetchingAPI, apiService } from '@/api'
import { toast } from '@/components/toasts/toast.helpers'

// GET a usage-stats endpoint with the shared date/org/timezone filter. Org
// omitted spans every org (where the endpoint allows it); ``timezone`` (IANA
// name) buckets the dates as the viewer's local calendar days, not UTC.
const getUsageStats =
  (path, { startDate, endDate, organizationId, timezone }) =>
  (dispatch) => {
    const params = queryString.stringify({
      start_date: startDate,
      end_date: endDate,
      requested_organization_id: organizationId ?? undefined,
      timezone: timezone || undefined,
    })
    return fetchingAPI(`${apiService.kodi}/${path}?${params}`, 'GET', dispatch)
  }

// Card aggregates over the range (org omitted spans every org).
export const fetchKodiUsageStats = (opts) => getUsageStats('usage-stats', opts)

// An org's Kodi sessions with activity in the range. Org-scoped (the endpoint
// rejects an all-orgs request), so only call it with an org selected.
export const fetchKodiUsageSessions = (opts) => getUsageStats('usage-stats/sessions', opts)

// Per-day active vs. created session counts for one org. Org-scoped.
export const fetchKodiUsageSessionsByDay = (opts) =>
  getUsageStats('usage-stats/sessions-by-day', opts)

// Negative-feedback turns over the range (org omitted spans every org). Each row
// carries its org, username, and session.
export const fetchKodiNegativeFeedback = (opts) =>
  getUsageStats('usage-stats/negative-feedback', opts)

// An org's policies with rolling-window usage. Requires a specific org (the
// endpoint rejects an all-orgs request), so only call it with an org selected.
export const fetchKodiUsagePolicies = (organizationId) => (dispatch) => {
  const params = queryString.stringify({ requested_organization_id: organizationId })
  return fetchingAPI(`${apiService.kodi}/usage-policies?${params}`, 'GET', dispatch)
}

// PATCH a usage policy's token limit and/or enabled flag; resolves with the
// updated policy. Scope (org/user/window) is fixed at creation, so only these
// two are editable.
export const updateUsagePolicy =
  ({ policyId, organizationId, tokenLimit, enabled }) =>
  (dispatch) => {
    const params = queryString.stringify({
      policy_id: policyId,
      requested_organization_id: organizationId ?? undefined,
    })
    return fetchingAPI(
      `${apiService.kodi}/usage-policies?${params}`,
      'PATCH',
      dispatch,
      JSON.stringify({ token_limit: tokenLimit, enabled })
    )
  }

// POST a new usage policy for an org (userId null = org-wide); resolves with it.
export const createUsagePolicy =
  ({ organizationId, userId, windowHours, tokenLimit, enabled }) =>
  (dispatch) => {
    const params = queryString.stringify({ requested_organization_id: organizationId ?? undefined })
    return fetchingAPI(
      `${apiService.kodi}/usage-policies?${params}`,
      'POST',
      dispatch,
      JSON.stringify({
        user_id: userId,
        window_hours: windowHours,
        token_limit: tokenLimit,
        enabled,
      })
    )
  }

// Audit trail for one assistant response (org-1 dev mode; the endpoint 403s
// other orgs). Resolves to the audit record; the caller handles failures.
export const fetchAudit = (auditUuid) => (dispatch) =>
  fetchingAPI(`${apiService.kodi}/audit/${encodeURIComponent(auditUuid)}`, 'GET', dispatch)

// ``type`` is 'positive' | 'negative'. Only negative is wired up today (the
// thumbs-down control); the endpoint/param support positive for a future
// thumbs-up. ``categories`` (fixed labels) and ``comment`` (free text) are sent
// as separate keys — the backend stores categories plaintext and encrypts the
// comment. Resolves to whether the write succeeded so the caller can flip the
// control to its submitted state.
export const submitFeedback = (auditUuid, type, categories, comment) => async (dispatch) => {
  const trimmed = comment?.trim()
  const body = { type, categories }
  if (!isEmpty(trimmed)) {
    body.comment = trimmed
  }
  try {
    await fetchingAPI(
      `${apiService.kodi}/feedback/${encodeURIComponent(auditUuid)}`,
      'PATCH',
      dispatch,
      JSON.stringify(body)
    )
    toast.success('Thanks for the feedback')
    return true
  } catch {
    toast.error('Could not submit feedback')
    return false
  }
}
